import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, X, ArrowRight, MessageCircle, Zap } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/hooks/useAuth";

// Each competitor's positioning + feature matrix.
const COMPETITORS = {
  quadlayers: {
    name: "QuadLayers",
    tagline: "WhatsApp Chat plugin for WordPress",
    summary: "QuadLayers' WhatsApp Chat plugin opens the visitor's WhatsApp app and starts a thread with a human on your team. It's a popular free WordPress plugin (500K+ installs) — but you still have to reply yourself.",
    one_liner: "QuadLayers lets visitors message you. AgentReply replies for you — 24/7, in your voice, even when you're asleep.",
    metric: "5,000+ WordPress sites use QuadLayers, but their owners still answer every message manually.",
  },
  manychat: {
    name: "ManyChat",
    tagline: "Decision-tree chatbot builder",
    summary: "ManyChat is one of the biggest names in Instagram & Messenger automation. It's flow-based — you build conversations as decision trees with buttons and quick replies. Powerful, but rigid: every customer journey has to be pre-mapped.",
    one_liner: "ManyChat needs you to design every conversation upfront. AgentReply just understands what people say — no flowcharts.",
    metric: "ManyChat's pro tier starts at $15/mo and limits you to 1,000 contacts. AgentReply gives you unlimited contacts from day one.",
  },
  tidio: {
    name: "Tidio",
    tagline: "Live chat & chatbot platform",
    summary: "Tidio combines live chat with simple AI bots. Great for e-commerce on Shopify, but the AI features sit behind their top-tier 'Lyro' add-on at $39/mo extra, with limits on conversations and intent training.",
    one_liner: "Tidio's AI is a $39/mo upsell. AgentReply's AI is the entire product — and it costs less than that.",
    metric: "Tidio's Lyro AI starts at $39/mo for 50 conversations. AgentReply is $9.99/mo with no conversation cap.",
  },
};

const FEATURES = [
  { key: "ai_replies", label: "AI replies on its own (no human needed)", us: true, them: { quadlayers: false, manychat: "limited", tidio: "addon" } },
  { key: "no_decision_tree", label: "Real conversations (no flowcharts to build)", us: true, them: { quadlayers: false, manychat: false, tidio: "limited" } },
  { key: "whatsapp_cloud", label: "Native WhatsApp Business Cloud API", us: true, them: { quadlayers: false, manychat: true, tidio: false } },
  { key: "instagram", label: "Instagram Direct Messages", us: true, them: { quadlayers: false, manychat: true, tidio: false } },
  { key: "messenger", label: "Facebook Messenger", us: true, them: { quadlayers: false, manychat: true, tidio: true } },
  { key: "website_widget", label: "Website embed widget", us: true, them: { quadlayers: true, manychat: false, tidio: true } },
  { key: "kb", label: "Knowledge-base upload (PDF / CSV / TXT)", us: true, them: { quadlayers: false, manychat: false, tidio: "limited" } },
  { key: "voice", label: "Voice replies (9 voices)", us: true, them: { quadlayers: false, manychat: false, tidio: false } },
  { key: "crm_memory", label: "Per-contact CRM memory", us: true, them: { quadlayers: false, manychat: true, tidio: true } },
  { key: "intake", label: "Structured intake forms", us: true, them: { quadlayers: false, manychat: true, tidio: "limited" } },
  { key: "pause_ai", label: "Pause AI per-contact for VIPs", us: true, them: { quadlayers: false, manychat: false, tidio: "limited" } },
  { key: "branding", label: "Full white-label branding", us: true, them: { quadlayers: "limited", manychat: false, tidio: "addon" } },
  { key: "honest_price", label: "Sub-$10/mo entry plan", us: true, them: { quadlayers: true, manychat: false, tidio: false } },
  { key: "lifetime", label: "One-time lifetime plan available", us: true, them: { quadlayers: false, manychat: false, tidio: false } },
];

function Tick({ value }) {
  if (value === true) return <Check className="w-5 h-5 text-[#D95B38]" strokeWidth={2} />;
  if (value === false) return <X className="w-5 h-5 text-[#7A7068]" strokeWidth={1.5} />;
  if (value === "limited") return <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8F]">Limited</span>;
  if (value === "addon") return <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8F]">Paid add-on</span>;
  return null;
}

export default function ComparePage() {
  const { competitor } = useParams();
  const { user, logout } = useAuth({ redirectIfUnauthed: false });
  const comp = COMPETITORS[competitor] || COMPETITORS.quadlayers;
  const slug = competitor || "quadlayers";

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB]">
      <Helmet>
        <title>{`AgentReply vs ${comp.name} — Which AI is best for WhatsApp?`}</title>
        <meta name="description" content={`Compare AgentReply and ${comp.name} side-by-side. See which is best for replying on WhatsApp, Instagram and your website. ${comp.one_liner.slice(0, 80)}`} />
        <link rel="canonical" href={`https://agentreply.io/vs/${slug}`} />
        <meta property="og:title" content={`AgentReply vs ${comp.name} — feature-by-feature comparison`} />
        <meta property="og:description" content={comp.one_liner} />
        <meta property="og:url" content={`https://agentreply.io/vs/${slug}`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": `AgentReply vs ${comp.name}`,
          "description": `Side-by-side comparison of AgentReply and ${comp.name} for AI-powered WhatsApp, Instagram and website chat.`,
          "url": `https://agentreply.io/vs/${slug}`,
        })}</script>
      </Helmet>

      <Navbar user={user} onLogout={logout} />

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="compare-page">
        <header className="mb-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-4 flex items-center gap-2">
            <Zap className="w-3 h-3" strokeWidth={2} />Comparison
          </div>
          <h1 className="font-serif text-5xl md:text-6xl mb-6 leading-[1.05]">
            AgentReply <em className="italic text-[#A39A8F]">vs</em> {comp.name}.
          </h1>
          <p className="font-mono text-base text-[#A39A8F] leading-relaxed max-w-3xl mb-4">{comp.summary}</p>
          <div className="bg-[#1A1715] border-l-4 border-[#D95B38] p-6 my-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-2">In one line</div>
            <p className="font-serif text-xl md:text-2xl text-[#F5F2EB] leading-snug">{comp.one_liner}</p>
          </div>
          <p className="font-mono text-xs text-[#7A7068] italic">{comp.metric}</p>
        </header>

        <section className="mb-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-4">Feature comparison</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-8">Side by side.</h2>
          <div className="bg-[#1A1715] border border-[#F5F2EB]/10 overflow-x-auto">
            <table className="w-full" data-testid="compare-table">
              <thead>
                <tr className="border-b border-[#F5F2EB]/10">
                  <th className="text-left p-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Feature</th>
                  <th className="text-center p-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] min-w-[120px]">AgentReply</th>
                  <th className="text-center p-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] min-w-[120px]">{comp.name}</th>
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((f) => (
                  <tr key={f.key} className="border-b border-[#F5F2EB]/5 last:border-0">
                    <td className="p-5 font-mono text-sm text-[#E0D8CA]">{f.label}</td>
                    <td className="p-5 text-center"><div className="flex items-center justify-center"><Tick value={f.us} /></div></td>
                    <td className="p-5 text-center"><div className="flex items-center justify-center"><Tick value={f.them[slug]} /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="bg-[#1A1715] border border-[#D95B38]/30 p-8 md:p-12 mb-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3">Try it free</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-3 leading-tight">See the difference yourself.</h2>
          <p className="font-mono text-sm text-[#A39A8F] mb-6 max-w-2xl leading-relaxed">
            Spin up an AgentReply agent in five minutes. 14-day free trial — no card required. Real conversations on day one.
          </p>
          <div className="flex gap-3 flex-wrap">
            <Link to="/signup" data-testid="compare-cta-signup" className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 btn-press inline-flex items-center gap-2">
              Start free trial <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link to="/help/whatsapp" className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 px-6 py-3 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp setup guide
            </Link>
          </div>
        </section>

        <section className="mb-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-4">Also comparing against</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(COMPETITORS).filter(([k]) => k !== slug).map(([k, c]) => (
              <Link key={k} to={`/vs/${k}`} className="bg-[#1A1715] border border-[#F5F2EB]/10 hover:border-[#D95B38]/40 transition-colors p-6 block" data-testid={`compare-link-${k}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] mb-2">vs {c.name}</div>
                <div className="font-serif text-lg text-[#F5F2EB] mb-2">{c.tagline}</div>
                <div className="font-mono text-xs text-[#A39A8F]">See comparison →</div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
